import React, { useEffect, useRef, useState } from "react";
import {
  Camera, Upload, Loader2, X, CheckSquare, Square, FileText,
  Calculator, AlertTriangle, Download, CheckCircle2, MapPin, Mic, MicOff, Lock,
  Sparkles, Check, RefreshCcw, History, ChevronDown, ChevronRight, Tag,
} from "lucide-react";
import api, { API, getToken } from "@/lib/api";
import jsPDF from "jspdf";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString("sv-SE").replace(/,/g, " ") + " kr";

/**
 * Fältinspektionspanel för tillsynsärenden – foton, checklista, anteckningar,
 * sanktionsberäkning. Renderas inline i TillsynDetail.
 */
export default function TillsynInspection({ tillsynId, tillsyn }) {
  const [photos, setPhotos] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [notes, setNotes] = useState("");
  const [sanction, setSanction] = useState(null);
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [showSanction, setShowSanction] = useState(false);
  const [showFinalize, setShowFinalize] = useState(false);
  const [listening, setListening] = useState(false);
  const [formalizeOpen, setFormalizeOpen] = useState(false);
  const [formalizing, setFormalizing] = useState(false);
  const [formalizeResult, setFormalizeResult] = useState(null);
  const [notesHistory, setNotesHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editingTagsFor, setEditingTagsFor] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const recognitionRef = useRef(null);

  const isLocked = !!signature;

  const load = async () => {
    try {
      const { data } = await api.get(`/tillsyn/${tillsynId}/inspection`);
      setPhotos(data.photos || []);
      setChecklist(data.checklist || []);
      setNotes(data.field_notes || "");
      setNotesHistory(data.notes_history || []);
      setSanction(data.sanction_calculation || null);
      setSignature(data.inspection_signature || tillsyn?.inspection_signature || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tillsynId]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    // Try to grab GPS – ok if user denies
    let gps = null;
    try {
      if (navigator.geolocation) {
        gps = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy_m: pos.coords.accuracy,
              heading: pos.coords.heading,
            }),
            () => resolve(null),
            { timeout: 5000, enableHighAccuracy: true }
          );
        });
      }
    } catch { /* ignore */ }
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (gps) {
        fd.append("lat", gps.lat);
        fd.append("lng", gps.lng);
        if (gps.accuracy_m != null) fd.append("accuracy_m", gps.accuracy_m);
        if (gps.heading != null) fd.append("heading", gps.heading);
      }
      const res = await api.post(`/tillsyn/${tillsynId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const tags = res.data?.tags || [];
      const parts = [];
      if (gps) parts.push("GPS");
      if (tags.length) parts.push(`taggar: ${tags.join(", ")}`);
      toast.success(parts.length ? `Bild uppladdad (${parts.join(" · ")})` : "Bild uppladdad");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Ta bort bilden?")) return;
    try {
      await api.delete(`/tillsyn/${tillsynId}/photos/${photoId}`);
      toast.success("Bild borttagen");
      await load();
    } catch {
      toast.error("Kunde inte ta bort bild");
    }
  };

  const toggleItem = async (key) => {
    const updated = checklist.map((c) =>
      c.key === key ? { ...c, checked: !c.checked } : c
    );
    setChecklist(updated);
    try {
      const { data } = await api.put(`/tillsyn/${tillsynId}/checklist`, {
        items: updated.map((c) => ({ key: c.key, checked: c.checked })),
      });
      // Use server response so checked_by/checked_at populates
      const byKey = Object.fromEntries((data.checklist || []).map((it) => [it.key, it]));
      setChecklist(updated.map((c) => ({ ...c, ...(byKey[c.key] || {}) })));
    } catch {
      toast.error("Kunde inte spara");
      await load();
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/tillsyn/${tillsynId}/notes`, { notes });
      toast.success("Anteckningar sparade");
    } catch {
      toast.error("Kunde inte spara anteckningar");
    } finally {
      setSavingNotes(false);
    }
  };

  const runFormalize = async () => {
    if ((notes || "").trim().length < 10) {
      toast.error("Skriv minst 10 tecken innan du formaliserar");
      return;
    }
    setFormalizeOpen(true);
    setFormalizing(true);
    setFormalizeResult(null);
    try {
      const { data } = await api.post(`/tillsyn/${tillsynId}/notes/formalize`, { notes });
      setFormalizeResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Formalisering misslyckades");
      setFormalizeOpen(false);
    } finally {
      setFormalizing(false);
    }
  };

  const acceptFormalized = async () => {
    if (!formalizeResult?.formalized) return;
    try {
      await api.post(`/tillsyn/${tillsynId}/notes/apply-formalized`, { text: formalizeResult.formalized });
      setNotes(formalizeResult.formalized);
      toast.success("Formaliserad text sparad – originalet finns i historiken");
      setFormalizeOpen(false);
      setFormalizeResult(null);
      await load(); // Refresh notes_history
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte spara");
    }
  };

  const toggleSpeech = () => {    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Röstinmatning stöds inte i den här webbläsaren (använd Chrome/Edge)");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = "sv-SE";
    rec.continuous = true;
    rec.interimResults = true;
    let finalTranscript = notes ? notes + " " : "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTranscript += t + " ";
        else interim += t;
      }
      setNotes(finalTranscript + interim);
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => { setListening(false); toast.error("Röstfel: " + e.error); };
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
    toast.info("Talar in på svenska – tryck igen för att stoppa.");
  };

  const finalize = async (outcome, note) => {
    try {
      await api.post(`/tillsyn/${tillsynId}/finalize`, { outcome, outcome_note: note || null });
      toast.success("Tillsynen är slutförd & signerad");
      setShowFinalize(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte slutföra");
    }
  };

  if (loading) {
    return <div className="text-sm text-zinc-500 py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laddar inspektion...</div>;
  }

  return (
    <div className="space-y-6" data-testid="tillsyn-inspection">
      {/* Signed banner */}
      {signature && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-sm p-4" data-testid="signed-banner">
          <Lock className="w-5 h-5 text-emerald-700 mt-0.5" />
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-800">Signerat & låst</div>
            <div className="text-sm text-emerald-900 mt-0.5">
              {signature.outcome_label} – signerat av <strong>{signature.signed_by}</strong> · {new Date(signature.signed_at).toLocaleString("sv-SE")}
            </div>
            {signature.note && <div className="text-xs text-emerald-800 mt-1">{signature.note}</div>}
          </div>
        </div>
      )}

      {/* ---------- Foton ---------- */}
      <section className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Camera className="w-4 h-4 text-blue-700" /> Fältfoton ({photos.length})
          </h3>
          <div className="flex gap-2">
            <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm ${
              isLocked ? "bg-zinc-200 text-zinc-500 cursor-not-allowed" : "bg-blue-700 hover:bg-blue-800 text-white cursor-pointer"
            }`}>
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                disabled={isLocked}
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                className="hidden" data-testid="inspection-camera-input" />
              Ta bild {!isLocked && <span className="text-[10px] opacity-70">(+GPS)</span>}
            </label>
            <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-sm ${
              isLocked ? "border-zinc-200 text-zinc-400 cursor-not-allowed" : "border-zinc-300 hover:bg-zinc-50 cursor-pointer"
            }`}>
              <Upload className="w-3.5 h-3.5" />
              <input ref={fileRef} type="file" accept="image/*" disabled={isLocked}
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                className="hidden" data-testid="inspection-upload-input" />
              Ladda upp
            </label>
          </div>
        </div>
        {photos.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center bg-zinc-50 rounded-sm">
            Inga bilder än. Använd &quot;Ta bild&quot; på mobil för att fota direkt på plats.
          </p>
        ) : (
          <>
            {(() => {
              const allTags = Array.from(new Set(photos.flatMap((p) => p.tags || []))).sort();
              if (allTags.length === 0) return null;
              const counts = Object.fromEntries(allTags.map((t) => [t, photos.filter((p) => (p.tags || []).includes(t)).length]));
              return (
                <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="photo-tag-filter-bar">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500 mr-1">Filtrera:</span>
                  <button onClick={() => setTagFilter(null)} data-testid="photo-tag-filter-all"
                    className={`px-2 py-0.5 text-xs rounded-sm border transition ${
                      tagFilter === null ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-50 text-zinc-700"
                    }`}>
                    Alla ({photos.length})
                  </button>
                  {allTags.map((t) => (
                    <button key={t} onClick={() => setTagFilter(tagFilter === t ? null : t)}
                      data-testid={`photo-tag-filter-${t}`}
                      className={`px-2 py-0.5 text-xs rounded-sm border transition ${
                        tagFilter === t ? "bg-purple-700 text-white border-purple-700" : "border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-800"
                      }`}>
                      {t} ({counts[t]})
                    </button>
                  ))}
                </div>
              );
            })()}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos
              .filter((p) => !tagFilter || (p.tags || []).includes(tagFilter))
              .map((p) => (
              <div key={p.photo_id} className="relative group aspect-square bg-zinc-100 rounded-sm overflow-hidden border border-zinc-200"
                   data-testid={`photo-${p.photo_id}`}>
                <img
                  src={`${API}/tillsyn/${tillsynId}/photos/${p.photo_id}/download?auth=${getToken()}`}
                  alt={p.label || p.filename}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                {p.gps && (
                  <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600 text-white text-[10px] rounded-sm">
                    <MapPin className="w-2.5 h-2.5" /> GPS
                  </div>
                )}
                {!isLocked && (
                  <>
                    <button onClick={() => setEditingTagsFor(p)}
                      data-testid={`photo-tags-btn-${p.photo_id}`}
                      title="Redigera taggar"
                      className="absolute top-1.5 right-8 p-1 bg-purple-700/80 hover:bg-purple-700 text-white rounded-sm opacity-0 group-hover:opacity-100 transition">
                      <Tag className="w-3 h-3" />
                    </button>
                    <button onClick={() => deletePhoto(p.photo_id)}
                      className="absolute top-1.5 right-1.5 p-1 bg-zinc-900/70 hover:bg-zinc-900 text-white rounded-sm opacity-0 group-hover:opacity-100 transition">
                      <X className="w-3 h-3" />
                    </button>
                  </>
                )}
                {(p.tags || []).length > 0 && (
                  <div className="absolute top-8 left-1.5 flex flex-wrap gap-1 max-w-[calc(100%-1rem)]" data-testid={`photo-tags-${p.photo_id}`}>
                    {(p.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="inline-flex items-center px-1.5 py-0.5 bg-purple-700/85 text-white text-[9px] rounded-sm font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-zinc-900/80 to-transparent px-2 py-1.5 text-[10px] text-white">
                  {p.uploaded_by_name} · {new Date(p.uploaded_at).toLocaleDateString("sv-SE")}
                  {p.gps && (
                    <div className="text-[9px] opacity-80 truncate">
                      {p.gps.lat?.toFixed(5)}, {p.gps.lng?.toFixed(5)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </section>

      {/* ---------- Checklist (rätt/fel) ---------- */}
      <section className="bg-white border border-zinc-200 rounded-sm p-6">
        <h3 className="text-lg font-medium flex items-center gap-2 mb-4" style={{ fontFamily: "Outfit" }}>
          <CheckSquare className="w-4 h-4 text-blue-700" /> Bedömning (kryssrutor)
        </h3>
        <ul className="space-y-1">
          {checklist.map((it) => (
            <li key={it.key}>
              <button onClick={() => toggleItem(it.key)}
                data-testid={`inspection-item-${it.key}`}
                className={`w-full flex items-start gap-3 p-2.5 rounded-sm transition text-left ${
                  it.checked ? "bg-amber-50 hover:bg-amber-100/70" : "hover:bg-zinc-50"
                }`}>
                {it.checked
                  ? <CheckSquare className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  : <Square className="w-4 h-4 text-zinc-300 stroke-[1.5] mt-0.5 flex-shrink-0" />}
                <div className="flex-1">
                  <div className={`text-sm ${it.checked ? "text-zinc-900 font-medium" : "text-zinc-700"}`}>
                    {it.label}
                  </div>
                  {it.checked && it.checked_by && (
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      {it.checked_by}
                      {it.checked_at && ` · ${new Date(it.checked_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}`}
                    </div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* ---------- Anteckningar ---------- */}
      <section className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-lg font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <FileText className="w-4 h-4 text-blue-700" /> Fältanteckningar
          </h3>
          {!isLocked && (
            <button onClick={toggleSpeech} data-testid="speech-toggle"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border transition ${
                listening ? "bg-red-50 border-red-300 text-red-700 animate-pulse" : "border-zinc-300 hover:bg-zinc-50"
              }`}>
              {listening ? <><MicOff className="w-3.5 h-3.5" /> Stoppa</> : <><Mic className="w-3.5 h-3.5" /> Diktera på svenska</>}
            </button>
          )}
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Observationer, samtal med ägare, mätningar, förslag till beslut..."
          rows={6}
          disabled={isLocked}
          data-testid="inspection-notes"
          className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-100 text-sm resize-none disabled:bg-zinc-50 disabled:text-zinc-600"
        />
        {!isLocked && (
          <div className="mt-3 flex justify-end gap-2 flex-wrap">
            <button onClick={runFormalize} disabled={formalizing || (notes || "").trim().length < 10}
              data-testid="formalize-notes-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-700 hover:bg-purple-800 text-white rounded-sm disabled:opacity-60 disabled:cursor-not-allowed"
              title="Låt Claude 4.5 skriva om anteckningen som formell juridisk prosa">
              {formalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Formalisera med AI
            </button>
            <button onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm disabled:opacity-60">
              {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Spara
            </button>
          </div>
        )}
      </section>

      {/* ---------- Formaliseringshistorik ---------- */}
      {notesHistory.length > 0 && (
        <section className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="notes-history-section">
          <button onClick={() => setHistoryOpen(!historyOpen)} data-testid="notes-history-toggle"
            className="w-full flex items-center justify-between gap-3 text-left group">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-purple-700" />
              <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>
                Formaliseringshistorik
              </h3>
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-sm border border-purple-200">
                {notesHistory.length} {notesHistory.length === 1 ? "händelse" : "händelser"}
              </span>
            </div>
            {historyOpen ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
          </button>
          {historyOpen && (
            <ol className="mt-4 space-y-3 border-l-2 border-purple-200 pl-4" data-testid="notes-history-list">
              {[...notesHistory].reverse().map((h, i) => (
                <li key={i} className="relative">
                  <div className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full bg-purple-500 border-2 border-white" />
                  <div className="text-xs text-zinc-500 mb-2">
                    <span className="font-medium text-zinc-700">{h.by || "Okänd"}</span>
                    {h.action === "ai_formalize" && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded-sm">
                        <Sparkles className="w-2.5 h-2.5" /> AI-formalisering
                      </span>
                    )}
                    {h.at && (
                      <span className="ml-1.5">
                        · {new Date(h.at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Original</div>
                      <div className="text-xs bg-zinc-50 border border-zinc-200 rounded-sm p-2.5 whitespace-pre-wrap text-zinc-700 max-h-40 overflow-y-auto">
                        {h.original || "(tom)"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-purple-600 mb-1">Formaliserad</div>
                      <div className="text-xs bg-purple-50 border border-purple-200 rounded-sm p-2.5 whitespace-pre-wrap text-zinc-900 max-h-40 overflow-y-auto">
                        {h.formalized || "(tom)"}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {/* ---------- Sanktion ---------- */}
      <section className={`border rounded-sm p-6 ${
        sanction ? "bg-red-50 border-red-200" : "bg-white border-zinc-200"
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Calculator className="w-4 h-4 text-blue-700" /> Sanktionsavgift
          </h3>
          <button onClick={() => setShowSanction(!showSanction)} data-testid="toggle-sanction-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-700 hover:bg-blue-800 text-white rounded-sm">
            {sanction ? "Räkna om" : "Beräkna avgift"}
          </button>
        </div>

        {sanction && !showSanction && (
          <SanctionSummary sanction={sanction} tillsyn={tillsyn} />
        )}

        {showSanction && (
          <SanctionInlineForm
            tillsynId={tillsynId}
            onComplete={(result) => {
              setSanction(result);
              setShowSanction(false);
              toast.success("Sanktion beräknad och sparad");
            }}
            onCancel={() => setShowSanction(false)}
          />
        )}

        {!sanction && !showSanction && (
          <p className="text-sm text-zinc-500">
            Beräkna byggsanktionsavgift baserat på fältinspektionen.
            Resultatet sparas på ärendet.
          </p>
        )}
      </section>

      {/* ---------- Dokumentmallar (PDF) ---------- */}
      <section className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="pdf-templates-section">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <FileText className="w-5 h-5" /> Dokumentmallar
            </h3>
            <p className="mt-1 text-sm text-zinc-500 max-w-xl">
              Generera juridiskt förformaterade PDF:er direkt från inspektionsdata –
              färdiga att skickas till fastighetsägaren eller diarieföras.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <PdfCard
            title="Föreläggande"
            desc="Beslut om åtgärd enligt PBL 11 kap. Inkluderar sanktionsbelopp och tidsfrist."
            testid="pdf-forelaggande-btn"
            tillsynId={tillsynId}
            template="forelaggande"
          />
          <PdfCard
            title="Förbud"
            desc="Beslut om förbud att fortsätta olovligt byggande enligt PBL 11 kap. 30–33 §§."
            testid="pdf-forbud-btn"
            tillsynId={tillsynId}
            template="forbud"
          />
          <PdfCard
            title="Tillsynsprotokoll"
            desc="Fullständigt protokoll från platsbesöket med foton, GPS och fältanteckningar."
            testid="pdf-protokoll-btn"
            tillsynId={tillsynId}
            template="protokoll"
          />
        </div>
      </section>

      {/* ---------- Slutför & signera ---------- */}
      {!isLocked && (
        <section className="bg-blue-50 border border-blue-200 rounded-sm p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-medium flex items-center gap-2 text-blue-900" style={{ fontFamily: "Outfit" }}>
                <Lock className="w-4 h-4" /> Slutför &amp; signera
              </h3>
              <p className="mt-1 text-sm text-blue-900/80 max-w-xl">
                När fältkontrollen är klar – låser ärendet, tidsstämplas med ditt namn och skickas till diariet.
                Sparar 2 timmars kontorsarbete efter varje inspektion.
              </p>
            </div>
            <button onClick={() => setShowFinalize(true)} data-testid="finalize-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm">
              <Lock className="w-4 h-4" /> Slutför &amp; signera
            </button>
          </div>
        </section>
      )}

      {showFinalize && (
        <FinalizeModal
          onCancel={() => setShowFinalize(false)}
          onConfirm={finalize}
          hasPhotos={photos.length > 0}
          hasSanction={!!sanction}
        />
      )}

      {formalizeOpen && (
        <FormalizeModal
          loading={formalizing}
          result={formalizeResult}
          original={notes}
          onRetry={runFormalize}
          onAccept={acceptFormalized}
          onCancel={() => { setFormalizeOpen(false); setFormalizeResult(null); }}
        />
      )}

      {editingTagsFor && (
        <PhotoTagsModal
          photo={editingTagsFor}
          tillsynId={tillsynId}
          onClose={() => setEditingTagsFor(null)}
          onSaved={(newTags) => {
            setPhotos((prev) => prev.map((ph) => ph.photo_id === editingTagsFor.photo_id ? { ...ph, tags: newTags } : ph));
            setEditingTagsFor(null);
          }}
        />
      )}
    </div>
  );
}


// ---------- PDF-mall-kort ----------
function PdfCard({ title, desc, testid, tillsynId, template }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post(
        `/tillsyn/${tillsynId}/pdf`,
        { template },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : `${template}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${title} genererad`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF-generering misslyckades");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={download} disabled={busy} data-testid={testid}
      className="text-left bg-white border border-zinc-200 hover:border-blue-400 rounded-sm p-4 transition disabled:opacity-60">
      <div className="flex items-start justify-between gap-2 mb-2">
        <FileText className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-zinc-400" /> : <Download className="w-3.5 h-3.5 text-zinc-400" />}
      </div>
      <div className="text-sm font-medium text-zinc-900">{title}</div>
      <div className="mt-1 text-[11px] text-zinc-500 leading-relaxed">{desc}</div>
    </button>
  );
}


// ---------- Formalize modal ----------
// ---------- Photo tags modal ----------
const PHOTO_TAG_SUGGESTIONS = [
  "oversikt", "detalj", "matning", "skada", "gransmarkering",
  "fasad", "tak", "konstruktion", "material", "dokument",
  "interior", "bygghandling",
];

function PhotoTagsModal({ photo, tillsynId, onClose, onSaved }) {
  const [tags, setTags] = useState(photo.tags || []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  const addTag = (t) => {
    const clean = t.trim().toLowerCase();
    if (!clean || tags.includes(clean) || tags.length >= 5) return;
    setTags([...tags, clean]);
    setInput("");
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/tillsyn/${tillsynId}/photos/${photo.photo_id}/tags`, { tags });
      onSaved(data.tags);
      toast.success("Taggar sparade");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm animate-fade-in-up" data-testid="photo-tags-modal">
        <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-purple-700" />
            <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Taggar för bilden</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-6 py-5 space-y-4">
          <p className="text-xs text-zinc-500">
            {photo.filename} — max 5 taggar. Handläggaren kan justera AI-genererade taggar.
          </p>
          <div className="flex flex-wrap gap-2 min-h-[36px] p-2 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="tags-current-list">
            {tags.length === 0 && <span className="text-xs text-zinc-400">Inga taggar än</span>}
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-sm border border-purple-200">
                {t}
                <button onClick={() => removeTag(t)} className="hover:bg-purple-200 rounded-sm" data-testid={`tag-remove-${t}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(input); }}}
              placeholder="Skriv en tagg och tryck Enter…"
              data-testid="tag-input"
              className="flex-1 px-3 py-2 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700"
            />
            <button onClick={() => addTag(input)} disabled={!input.trim() || tags.length >= 5}
              className="px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50 rounded-sm disabled:opacity-50">
              Lägg till
            </button>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Vanliga taggar</div>
            <div className="flex flex-wrap gap-1.5">
              {PHOTO_TAG_SUGGESTIONS.filter((t) => !tags.includes(t)).map((t) => (
                <button key={t} onClick={() => addTag(t)} disabled={tags.length >= 5}
                  data-testid={`tag-suggestion-${t}`}
                  className="px-2 py-0.5 text-xs border border-zinc-300 hover:border-purple-400 hover:bg-purple-50 rounded-sm disabled:opacity-50">
                  + {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        <footer className="px-6 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 hover:bg-zinc-100 rounded-sm">
            Avbryt
          </button>
          <button onClick={save} disabled={saving} data-testid="tags-save-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-700 hover:bg-purple-800 text-white rounded-sm disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Spara taggar
          </button>
        </footer>
      </div>
    </div>
  );
}


function FormalizeModal({ loading, result, original, onRetry, onAccept, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white border border-zinc-200 rounded-sm flex flex-col animate-fade-in-up" data-testid="formalize-modal">
        <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-700" />
            <div>
              <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>AI-formalisering av fältanteckningar</h3>
              <p className="text-xs text-zinc-500">Claude Sonnet 4.5 – juridisk myndighetssvenska enligt PBL/PBF</p>
            </div>
          </div>
          <button onClick={onCancel} data-testid="formalize-close" className="p-1.5 hover:bg-zinc-100 rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-zinc-500">
              <Loader2 className="w-8 h-8 animate-spin text-purple-700" />
              <p className="text-sm">Formaliserar anteckningen – detta tar 5–15 sekunder</p>
            </div>
          )}

          {!loading && result && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Original (fältanteckning)</h4>
                  <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-sm text-zinc-700 whitespace-pre-wrap min-h-[200px]" data-testid="formalize-original">
                    {original}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-purple-700 mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Formaliserad (AI-förslag)
                  </h4>
                  <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-sm text-zinc-900 whitespace-pre-wrap min-h-[200px]" data-testid="formalize-output">
                    {result.formalized || "(inget resultat)"}
                  </div>
                </div>
              </div>

              {result.changes_summary && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-blue-800 mb-1">Vad ändrades</div>
                  <p className="text-sm text-zinc-700 whitespace-pre-wrap">{result.changes_summary}</p>
                </div>
              )}

              {Array.isArray(result.law_refs) && result.law_refs.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Lagrumshänvisningar</div>
                  <div className="flex flex-wrap gap-1.5">
                    {result.law_refs.map((r, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 text-xs bg-zinc-100 border border-zinc-300 rounded-sm font-mono">{r}</span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(result.warnings) && result.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-800 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Varningar
                  </div>
                  <ul className="text-sm text-zinc-700 list-disc pl-4 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <footer className="px-6 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-between items-center gap-2 flex-wrap">
          <p className="text-xs text-zinc-500">
            Originalet sparas i historiken när du accepterar.
          </p>
          <div className="flex gap-2">
            <button onClick={onCancel} data-testid="formalize-cancel"
              className="px-4 py-2 text-sm border border-zinc-300 hover:bg-zinc-100 rounded-sm">
              Avbryt
            </button>
            <button onClick={onRetry} disabled={loading} data-testid="formalize-retry"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-zinc-300 hover:bg-zinc-100 rounded-sm disabled:opacity-60">
              <RefreshCcw className="w-3.5 h-3.5" /> Generera igen
            </button>
            <button onClick={onAccept} disabled={loading || !result?.formalized} data-testid="formalize-accept"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-700 hover:bg-purple-800 text-white rounded-sm disabled:opacity-60">
              <Check className="w-3.5 h-3.5" /> Ersätt anteckningen
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}


// ---------- Finalize modal ----------
function FinalizeModal({ onCancel, onConfirm, hasPhotos, hasSanction }) {
  const [outcome, setOutcome] = useState("avslutat_med_atgard");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await onConfirm(outcome, note);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up" data-testid="finalize-modal">
        <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Slutför &amp; signera inspektionen</h3>
        <p className="text-sm text-zinc-600 mb-4">
          När du signerar låses ärendet permanent. Ingen kan lägga till foton eller ändra bedömningen efteråt.
        </p>
        {(!hasPhotos || !hasSanction) && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-900 rounded-sm">
            OBS: {!hasPhotos && "Inga foton uppladdade. "} {!hasSanction && "Ingen sanktion beräknad."} Fortsätt endast om detta är avsiktligt.
          </div>
        )}
        <div className="space-y-2 mb-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Utfall</label>
          {[
            { v: "avslutat_med_atgard", l: "Avslutat med åtgärd" },
            { v: "avslutat_utan_atgard", l: "Avslutat utan åtgärd" },
            { v: "overklagat", l: "Överklagat" },
          ].map((o) => (
            <label key={o.v} className={`flex items-center gap-2 p-2.5 border rounded-sm cursor-pointer transition ${
              outcome === o.v ? "border-blue-700 bg-blue-50" : "border-zinc-200 hover:bg-zinc-50"
            }`}>
              <input type="radio" name="outcome" value={o.v} checked={outcome === o.v}
                onChange={(e) => setOutcome(e.target.value)}
                data-testid={`finalize-outcome-${o.v}`}
                className="accent-blue-700" />
              <span className="text-sm">{o.l}</span>
            </label>
          ))}
        </div>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Sluttext / motivering (valfritt)" data-testid="finalize-note"
          className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm resize-none focus:border-blue-700 outline-none" />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm hover:bg-zinc-100 rounded-sm">Avbryt</button>
          <button onClick={submit} disabled={busy} data-testid="finalize-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white text-sm rounded-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Signera &amp; lås
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------- Sanction inline form ----------
function SanctionInlineForm({ tillsynId, onComplete, onCancel }) {
  const [types, setTypes] = useState([]);
  const [actionType, setActionType] = useState("");
  const [area, setArea] = useState("");
  const [isOneOrTwoDwelling, setIsOneOrTwoDwelling] = useState(true);
  const [rectified, setRectified] = useState(false);
  const [voluntary, setVoluntary] = useState(false);
  const [hardship, setHardship] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sanction/action-types").then((r) => setTypes(r.data));
  }, []);

  const selectedType = types.find((t) => t.id === actionType);

  const submit = async (e) => {
    e.preventDefault();
    if (!actionType) return toast.error("Välj åtgärdstyp");
    setBusy(true);
    try {
      const { data } = await api.post("/sanction/calculate", {
        action_type: actionType,
        area_sqm: parseFloat(area) || 0,
        is_one_or_two_dwelling: isOneOrTwoDwelling,
        rectified, voluntary_disclosure: voluntary, economic_hardship: hardship,
        tillsyn_id: tillsynId,
      });
      onComplete(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Beräkning misslyckades");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="sanction-inline-form">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Åtgärdstyp</label>
        <select value={actionType} onChange={(e) => setActionType(e.target.value)}
          data-testid="inline-action-type"
          className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm bg-white text-sm focus:border-blue-700 outline-none">
          <option value="">— välj åtgärd —</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        {selectedType && (
          <p className="mt-1 text-[11px] text-zinc-500">Lagrum: {selectedType.law_ref}</p>
        )}
      </div>

      {selectedType?.requires_area && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Area (m²)</label>
          <input type="number" step="0.1" min="0" value={area} onChange={(e) => setArea(e.target.value)}
            data-testid="inline-area"
            placeholder={`Grundyta: ${selectedType.grund_yta_m2} m²`}
            className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:border-blue-700 outline-none" />
        </div>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={() => setIsOneOrTwoDwelling(true)}
          className={`flex-1 px-3 py-2 text-xs border rounded-sm ${
            isOneOrTwoDwelling ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-zinc-300"
          }`}>En-/tvåbostadshus</button>
        <button type="button" onClick={() => setIsOneOrTwoDwelling(false)}
          className={`flex-1 px-3 py-2 text-xs border rounded-sm ${
            !isOneOrTwoDwelling ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-zinc-300"
          }`}>Övrig byggnad</button>
      </div>

      <div className="space-y-1.5 pt-2 border-t border-zinc-100">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Nedsättningar</div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={rectified} onChange={(e) => setRectified(e.target.checked)}
            data-testid="inline-rectified" className="accent-blue-700" />
          Rättat (× 0.5)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={voluntary} onChange={(e) => setVoluntary(e.target.checked)}
            className="accent-blue-700" />
          Frivillig anmälan (× 0.75)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={hardship} onChange={(e) => setHardship(e.target.checked)}
            className="accent-blue-700" />
          Oskäligt belopp (× 0.5)
        </label>
      </div>

      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={busy || !actionType} data-testid="inline-calc-btn"
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm rounded-sm disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
          Beräkna &amp; spara
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">
          Avbryt
        </button>
      </div>
    </form>
  );
}


// ---------- Sanction summary (after calc) ----------
function SanctionSummary({ sanction, tillsyn }) {
  const exportPdf = () => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Beslut om byggsanktionsavgift", 14, 20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Tillsynsärende: ${tillsyn?.ref || ""}`, 14, 28);
    doc.text(`Adress: ${tillsyn?.address || "-"}`, 14, 34);
    const lines = doc.splitTextToSize(sanction.decision_text || "", 180);
    doc.text(lines, 14, 44);
    doc.save(`sanktion-${tillsyn?.ref || "beslut"}.pdf`);
  };

  return (
    <div data-testid="sanction-summary">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider font-semibold text-red-700">
            {sanction.verdict_label}
          </div>
          <div className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>
            {fmt(sanction.total)}
          </div>
          <div className="mt-1 text-xs text-zinc-600">{sanction.action_label}</div>
        </div>
      </div>

      <table className="mt-4 w-full text-xs">
        <tbody className="divide-y divide-zinc-200">
          <tr><td className="py-1.5 text-zinc-600">Grund</td><td className="py-1.5 text-right">{fmt(sanction.grund_belopp)}</td></tr>
          <tr><td className="py-1.5 text-zinc-600">Tillägg</td><td className="py-1.5 text-right">{fmt(sanction.tillagg_belopp)}</td></tr>
          {sanction.reductions?.map((r, i) => (
            <tr key={i}><td className="py-1.5 text-amber-700">↓ {r.reason}</td><td className="py-1.5 text-right text-amber-700">× {r.factor}</td></tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 text-[11px] text-zinc-500">
        Beräknat av {sanction.calculated_by} · {new Date(sanction.calculated_at).toLocaleString("sv-SE")}
      </div>

      <div className="mt-3 flex gap-2">
        <button onClick={exportPdf} data-testid="sanction-summary-pdf"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-900 text-white rounded-sm hover:bg-zinc-800">
          <Download className="w-3.5 h-3.5" /> PDF-beslut
        </button>
      </div>
    </div>
  );
}
